/**
 * ReturnChips — compact "↩ {label}" chips for DISPLACED unsaved artifact
 * drafts (chat-centric revamp T16). A session can hold several unsaved
 * (kind,target) drafts (an NVDA and an AAPL thesis, an import), but the bar
 * area shows one. Every draft the bar does not own renders here; tapping asks
 * the parent to restore that draft's snapshot into bar + sheet. Restoration
 * is local state only — the transcript stays the store and the next replay
 * recomputes the chips.
 */
import React, { useMemo } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { useTheme } from '../../contexts/ThemeContext';

export interface ReturnChipEntry {
  kind: 'thesis' | 'import' | 'profile';
  /** Thesis drafts key on their card symbol; import and profile (T24) are the
   * session-single slots (target null — they never chip while their bar owns
   * them). */
  target: string | null;
  label: string;
}

export function ReturnChips({
  entries,
  onRestore,
}: {
  entries: ReturnChipEntry[];
  onRestore: (entry: ReturnChipEntry) => void;
}) {
  const { theme } = useTheme();
  const styles = useMemo(() => createThemedStyles(theme), [theme]);
  if (entries.length === 0) return null;
  return (
    <View testID="return-chips" style={styles.row}>
      {entries.map((entry) => (
        <Pressable
          key={`${entry.kind}:${entry.target ?? '_'}`}
          testID={`return-chip-${entry.kind}-${entry.target ?? 'any'}`}
          onPress={() => onRestore(entry)}
          accessibilityRole="button"
          accessibilityLabel={entry.label}
          style={[
            styles.chip,
            { borderColor: theme.colors.border, backgroundColor: theme.colors.surface },
          ]}
        >
          <Text
            numberOfLines={1}
            style={{ color: theme.colors.primary, fontSize: theme.typography.fontSizeSm }}
          >
            ↩ {entry.label}
          </Text>
        </Pressable>
      ))}
    </View>
  );
}

function createThemedStyles(theme: any) {
  return StyleSheet.create({
    row: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: theme.spacing.xs,
      paddingHorizontal: theme.spacing.md,
      paddingVertical: theme.spacing.xs,
    },
    chip: {
      maxWidth: '100%',
      flexShrink: 1,
      minWidth: 0,
      paddingHorizontal: theme.spacing.sm,
      paddingVertical: theme.spacing.xs,
      borderRadius: theme.radii.full,
      borderWidth: 1,
    },
  });
}
